import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleAuth } from '../hooks/useGoogleAuth';
import './LoginForm.scss';

interface LoginFormProps {
  onSuccess?: () => void;
  redirectTo?: string;
}

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, redirectTo }) => {
  const [formData, setFormData] = useState({
    usernameOrEmail: '',
    password: '',
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const { googleAuthUrl, handleGoogleRedirect } = useGoogleAuth(redirectTo);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.usernameOrEmail.trim()) {
      newErrors.usernameOrEmail = 'Username or email is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await login(formData.usernameOrEmail, formData.password);
      
      if (result.success) {
        if (onSuccess) {
          onSuccess();
        } else if (redirectTo) {
          navigate(redirectTo);
        } else {
          navigate('/');
        }
      } else {
        setSubmitError(result.message || 'Login failed');
      }
    } catch (error) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-form-container">
      <div className="login-form-card">
        <div className="login-form-header">
          <h2 className="login-form-title">Welcome Back</h2>
          <p className="login-form-subtitle">Sign in to your account</p>
        </div>

        <Form onSubmit={handleSubmit} className="login-form">
          {submitError && (
            <Alert variant="danger" className="mb-3">
              {submitError}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label htmlFor="usernameOrEmail">
              <i className="fas fa-user me-2"></i>
              Username or Email
            </Form.Label>
            <Form.Control
              type="text"
              id="usernameOrEmail"
              name="usernameOrEmail"
              value={formData.usernameOrEmail}
              onChange={handleInputChange}
              isInvalid={!!errors.usernameOrEmail}
              placeholder="Enter your username or email"
              className="form-control-lg"
            />
            <Form.Control.Feedback type="invalid">
              {errors.usernameOrEmail}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label htmlFor="password">
              <i className="fas fa-lock me-2"></i>
              Password
            </Form.Label>
            <Form.Control
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              isInvalid={!!errors.password}
              placeholder="Enter your password"
              className="form-control-lg"
            />
            <Form.Control.Feedback type="invalid">
              {errors.password}
            </Form.Control.Feedback>
          </Form.Group>

          <div className="d-flex justify-content-between align-items-center mb-3">
            <Form.Check
              type="checkbox"
              id="rememberMe"
              label="Remember me"
              className="text-muted"
            />
            <Link to="/forgot-password/" className="text-decoration-none">
              Forgot password?
            </Link>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-100 login-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Signing in...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt me-2"></i>
                Sign In
              </>
            )}
          </Button>
        </Form>

        <div className="login-form-footer">
          <p className="text-center text-muted mb-0">
            Don't have an account?{' '}
            <Link to="/signup" className="text-decoration-none fw-bold">
              Sign up here
            </Link>
          </p>
        </div>

        <div className="login-form-divider">
          <span>or</span>
        </div>

        <div className="social-login">
          <Button
            type="button"
            variant="outline-primary"
            size="lg"
            className="w-100"
            onClick={handleGoogleRedirect(setSubmitError, 'Google login is not configured. Please try again later.')}
            disabled={isSubmitting || !googleAuthUrl}
          >
            <i className="fab fa-google me-2"></i>
            Continue with Google
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
